'use client'
import { createButton } from '@gluestack-ui/core/button/creator'
import { PrimitiveIcon } from '@gluestack-ui/core/icon/creator'
import {
    tva,
    useStyleContext,
    type VariantProps,
    withStyleContext,
} from '@gluestack-ui/utils/nativewind-utils'
import { cssInterop } from 'nativewind'
import React from 'react'
import { ActivityIndicator, Pressable, Text, View } from 'react-native'

import { UIIcon } from '../icon/UIIcon'

const SCOPE = 'BUTTON'

const Root = withStyleContext(Pressable, SCOPE)

const UIButton = createButton({
    Root: Root,
    Text,
    Group: View,
    Spinner: ActivityIndicator,
    Icon: UIIcon,
})

cssInterop(UIButton, { className: 'style' })
cssInterop(UIButton.Text, { className: 'style' })
cssInterop(UIButton.Group, { className: 'style' })

cssInterop(PrimitiveIcon, {
    className: {
        target: 'style',
        nativeStyleToProp: {
            height: true,
            width: true,
            fill: true,
            color: 'classNameColor',
            stroke: true,
        },
    },
})

// Structural scaffolding only. All presentation (bg, border, hover/active/focus
// colour, size, action/variant) lives in `components/button` — the single
// prop-driven source of truth. Keeping colour maps here leaked Gluestack's
// `data-[hover|active]:` state classes (a different modifier namespace than our
// `hover:`/`active:`, so tailwind-merge never dedupes them) onto every button.
// Deviation from upstream Gluestack: its focus treatment
// (`data-[focus-visible=true]:web:outline-none` + `:web:ring-2`) is dropped. Since
// AOS-995 registered the `web:` variant for web builds those rules do emit css, and
// they beat our tokenised ring on equal specificity — the outline was forced
// transparent and the visible ring became Tailwind's default blue instead of the
// per-tenant `action.focus-ring`. `components/button` owns the focus ring, as it
// owns every other visual, so keep this base free of focus styling.
const buttonStyle = tva({
    base: 'group/button rounded flex-row items-center justify-center data-[disabled=true]:opacity-40',
    variants: {
        action: { primary: '', secondary: '', positive: '', negative: '', default: '' },
        variant: { link: '', outline: '', solid: '' },
        size: { xs: '', sm: '', md: '', lg: '', xl: '' },
    },
})

// Label colour/weight is owned by `components/button` (resolveButtonLabel +
// buttonTextBase). Only the cross-platform select-none stays here.
const buttonTextStyle = tva({
    base: 'web:select-none',
    parentVariants: {
        action: { primary: '', secondary: '', positive: '', negative: '' },
        variant: { link: '', outline: '', solid: '' },
        size: { xs: '', sm: '', md: '', lg: '', xl: '' },
    },
})

// Icon colour/size is owned by `components/button` (ButtonIcon resolves hex +
// px from context). Only the cross-platform fill reset stays here.
const buttonIconStyle = tva({
    base: 'fill-none',
    parentVariants: {
        variant: { link: '', outline: '', solid: '' },
        size: { xs: '', sm: '', md: '', lg: '', xl: '' },
        action: { primary: '', secondary: '', positive: '', negative: '' },
    },
})

const buttonGroupStyle = tva({
    base: '',
    variants: {
        space: {
            'xs': 'gap-1',
            'sm': 'gap-2',
            'md': 'gap-3',
            'lg': 'gap-4',
            'xl': 'gap-5',
            '2xl': 'gap-6',
            '3xl': 'gap-7',
            '4xl': 'gap-8',
        },
        isAttached: {
            true: 'gap-0',
        },
        flexDirection: {
            'row': 'flex-row',
            'column': 'flex-col',
            'row-reverse': 'flex-row-reverse',
            'column-reverse': 'flex-col-reverse',
        },
    },
})

type IButtonProps = Omit<React.ComponentPropsWithoutRef<typeof UIButton>, 'context'> &
    VariantProps<typeof buttonStyle> & { className?: string }

const Button = React.forwardRef<React.ElementRef<typeof UIButton>, IButtonProps>(
    ({ className, variant = 'solid', size = 'md', action = 'primary', ...props }, ref) => {
        return (
            <UIButton
                ref={ref}
                {...props}
                className={buttonStyle({ variant, size, action, class: className })}
                context={{ variant, size, action }}
            />
        )
    },
)

type IButtonTextProps = React.ComponentPropsWithoutRef<typeof UIButton.Text> &
    VariantProps<typeof buttonTextStyle> & { className?: string }

const ButtonText = React.forwardRef<React.ElementRef<typeof UIButton.Text>, IButtonTextProps>(
    ({ className, variant, size, action, ...props }, ref) => {
        const {
            variant: parentVariant,
            size: parentSize,
            action: parentAction,
        } = useStyleContext(SCOPE)

        return (
            <UIButton.Text
                ref={ref}
                {...props}
                className={buttonTextStyle({
                    parentVariants: {
                        variant: parentVariant,
                        size: parentSize,
                        action: parentAction,
                    },
                    variant,
                    size,
                    action,
                    class: className,
                })}
            />
        )
    },
)

const ButtonSpinner = UIButton.Spinner

type IButtonIcon = React.ComponentPropsWithoutRef<typeof UIButton.Icon> &
    VariantProps<typeof buttonIconStyle> & {
        className?: string | undefined
        as?: React.ElementType
        height?: number
        width?: number
    }

const ButtonIcon = React.forwardRef<React.ElementRef<typeof UIButton.Icon>, IButtonIcon>(
    ({ className, size, ...props }, ref) => {
        const {
            variant: parentVariant,
            size: parentSize,
            action: parentAction,
        } = useStyleContext(SCOPE)

        if (typeof size === 'number') {
            return (
                <UIButton.Icon
                    ref={ref}
                    {...props}
                    className={buttonIconStyle({ class: className })}
                    size={size}
                />
            )
        } else if (
            (props.height !== undefined || props.width !== undefined) &&
            size === undefined
        ) {
            return (
                <UIButton.Icon
                    ref={ref}
                    {...props}
                    className={buttonIconStyle({ class: className })}
                />
            )
        }
        return (
            <UIButton.Icon
                {...props}
                className={buttonIconStyle({
                    parentVariants: {
                        size: parentSize,
                        variant: parentVariant,
                        action: parentAction,
                    },
                    size,
                    class: className,
                })}
                ref={ref}
            />
        )
    },
)

type IButtonGroupProps = React.ComponentPropsWithoutRef<typeof UIButton.Group> &
    VariantProps<typeof buttonGroupStyle>

const ButtonGroup = React.forwardRef<React.ElementRef<typeof UIButton.Group>, IButtonGroupProps>(
    ({ className, space = 'md', isAttached = false, flexDirection = 'column', ...props }, ref) => {
        return (
            <UIButton.Group
                className={buttonGroupStyle({
                    class: className,
                    space,
                    isAttached,
                    flexDirection,
                })}
                {...props}
                ref={ref}
            />
        )
    },
)

Button.displayName = 'Button'
ButtonText.displayName = 'ButtonText'
ButtonSpinner.displayName = 'ButtonSpinner'
ButtonIcon.displayName = 'ButtonIcon'
ButtonGroup.displayName = 'ButtonGroup'

export { Button, ButtonText, ButtonSpinner, ButtonIcon, ButtonGroup }
